import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { Checkbox } from '@/components/form/Checkbox';
import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useWizardFooterConfig,
  WizardModalFooter,
  WizardStepBody,
  WizardStepper,
} from '@/components/wizards';
import {
  ExportFormat,
  useExportReport,
  useValidateReport,
  ValidateReportError,
} from '@/hooks/useReportExport';
import {
  RiskModeStep,
  RiskScopeConfigStep,
} from '@/sections/export/components/RiskSelectionStep';
import {
  DEFAULT_SECTION_ORDER,
  useReportConfigForm,
} from '@/sections/export/hooks/useReportConfigForm';
import {
  RiskSelectionMode,
  useRiskSelection,
} from '@/sections/export/hooks/useRiskSelection';
import { useAuth } from '@/state/auth';
import { RiskStatus } from '@/types';

const getTodayISO = (): string => new Date().toISOString().split('T')[0];

// Radix Select forbids empty-string item values, so "" (= don't include) is
// represented by this sentinel.
const NONE_VALUE = '__none__';

// Sentinel executive_summary_path understood by the backend: the report job
// generates the executive summary from the report's findings via an LLM.
const AUTO_EXECUTIVE_SUMMARY = 'auto';

// Finding-sort options — value = the exact string sent in config.finding_sort.
const FINDING_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'default', label: 'Default (alphabetical)' },
  { value: 'cvss_desc', label: 'CVSS' },
];

// Section label lookup — key = the exact string sent in config.section_order.
// The order of THIS record is irrelevant; the live order is current.sectionOrder.
const SECTION_LABELS: Record<string, string> = {
  description: 'Description',
  impact: 'Impact',
  recommendation: 'Recommendation',
  references: 'References',
  proof: 'Proof / Evidence',
};

// Drop unknown/removed keys (e.g. a legacy "taxonomies" from a persisted
// pdf-export-options value) so they never render, reorder, or reach the backend.
function cleanSectionOrder(sectionOrder: string[]): string[] {
  return sectionOrder.filter(k => k in SECTION_LABELS);
}

// Whether the section order matches the standard order (Section order select
// starts in "Default" mode vs "Custom").
function isStandardSectionOrder(sectionOrder: string[]): boolean {
  return (
    sectionOrder.length === DEFAULT_SECTION_ORDER.length &&
    sectionOrder.every((key, i) => key === DEFAULT_SECTION_ORDER[i])
  );
}

export interface GenerateReportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type StepKey =
  | 'scope'
  | 'scopeConfig'
  | 'basics'
  | 'engagement'
  | 'files'
  | 'review';

const STEP_TITLES: Record<StepKey, string> = {
  scope: 'Select risks',
  scopeConfig: 'Configure scope',
  basics: 'Report basics',
  engagement: 'Engagement',
  files: 'Content files',
  review: 'Review & generate',
};

const STEP_DESCRIPTIONS: Record<StepKey, string> = {
  scope: 'Which risks should this export cover?',
  scopeConfig: 'Configure the risk scope for this report.',
  basics: 'Set the report title, format, grouping, and ordering.',
  engagement: 'Client, target, dates, and report flags.',
  files: 'Attach optional executive summary, narratives, and appendix.',
  review: 'Confirm the selection and report configuration, then generate.',
};

// The `scopeConfig` step exists only when the chosen mode needs configuring;
// `all` has no config, so Next on `scope` skips straight to `basics`. The step
// list is recomputed from the live mode each render, so the Configure dot
// appears/disappears automatically (the stepper is pure-presentational).
function buildSteps(mode: RiskSelectionMode): StepKey[] {
  return [
    'scope',
    ...(mode === 'all' ? [] : (['scopeConfig'] as StepKey[])),
    'basics',
    'engagement',
    'files',
    'review',
  ];
}

// Mode-aware copy for the config screen — keeps the header meaningful per mode.
function scopeConfigTitle(mode: RiskSelectionMode): string {
  if (mode === 'dateRange') return 'Date range';
  if (mode === 'byAsset') return 'Select assets';
  return 'Select vulnerabilities';
}
function scopeConfigDescription(mode: RiskSelectionMode): string {
  if (mode === 'dateRange')
    return 'Pick the date field and window this report should cover.';
  if (mode === 'byAsset')
    return 'Choose the assets whose open risks this report should cover.';
  return 'Hand-pick the exact risks this report should cover.';
}

/**
 * GenerateReportWizard — a Hannibal-style multi-step Dialog that replaces the
 * old report dropdown + modal. It generates a pentest PDF/ZIP report with
 * in-wizard risk selection.
 *
 * Externally controlled (like LaunchHannibalDialog): the parent owns `open` and
 * `onOpenChange` and renders its own trigger. Praetorian-only — returns `null`
 * for non-Praetorian users.
 */
export function GenerateReportWizard({
  open,
  onOpenChange,
}: GenerateReportWizardProps) {
  const { isPraetorianUser } = useAuth();

  if (!isPraetorianUser) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onOpenChange(false)}>
      <DialogContent
        className="max-w-3xl"
        // FilterDropdown's panel is portalled to document.body (see
        // FilterDropdown.tsx). Because that portal is a DOM sibling of this
        // DialogContent, Radix's DismissableLayer treats clicks inside the
        // dropdown as `pointerDownOutside` and dismisses the whole wizard.
        // The FilterDropdown contract stamps every portalled node with
        // `data-filter-dropdown-portal`; we honor it here so clicks inside
        // the dropdown are treated as inside the dialog while normal
        // outside-click-to-close still works everywhere else.
        onInteractOutside={event => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-filter-dropdown-portal]')) {
            event.preventDefault();
          }
        }}
      >
        <GenerateReportForm key={String(open)} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function GenerateReportForm({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const selection = useRiskSelection();

  // Navigate by a STABLE step key, not a numeric index: the step list changes
  // length with the mode (3 keys for `all`, 4 otherwise), so a frozen index
  // would drift. The current key stays valid across mode changes because mode
  // can only change on `scope`, where the key is `scope` for every mode.
  const [currentStep, setCurrentStep] = useState<StepKey>('scope');
  const steps = buildSteps(selection.mode);
  const currentIndex = Math.max(0, steps.indexOf(currentStep));

  const {
    current,
    handleFieldChange,
    persistFormValues,
    fileVariants,
    isDataLoading,
  } = useReportConfigForm();

  const [validationErrors, setValidationErrors] = useState<
    ValidateReportError[]
  >([]);

  const { mutateAsync: validateReport, status: validateStatus } =
    useValidateReport();
  const { mutateAsync: exportReport } = useExportReport();

  const isValidating = validateStatus === 'pending';

  // 'scope' always advances (a mode is always selected; default `all`).
  // 'scopeConfig' is navigable once the date-range scope (if any) has fully
  // resolved — blocking while `selection.isResolving`/`isLoadingOptions`
  // ensures the report never validates or exports against a partially-paged
  // (capped) key set. See useRiskSelection. The config steps
  // (basics/engagement/files) need the report-config data; gate Next until it's
  // loaded. 'review' additionally waits on validation + scope resolution before
  // it can submit.
  // A dateRange scope with blank bounds resolves to `undefined` (= all open
  // risks), so block Next until both dates are set. `specific` intentionally
  // allows an empty selection (= all open risks); `all` never reaches this step.
  // byAsset must resolve to at least one risk before advancing: an empty
  // `risk_keys` payload is interpreted server-side as "all open risks"
  // (report/service.go FetchRisks), so a scoped selection that resolved to
  // zero would silently export everything. Require a non-empty resolved set.
  // For `specific` mode we also gate on `isSpecificScopeReady`, which blocks
  // the zero-match tag-filter case (tag selected but matched zero open risks:
  // resolves to []; backend treats that as "all open risks" and would export
  // everything). See useRiskSelection.
  const scopeConfigReady =
    selection.mode === 'dateRange'
      ? Boolean(selection.startDate && selection.endDate)
      : selection.mode === 'byAsset'
        ? selection.selectedAssetKeys.length > 0 &&
          (selection.resolvedRiskKeys?.length ?? 0) > 0
        : selection.mode === 'specific'
          ? selection.isSpecificScopeReady
          : true;
  const canGoNext =
    currentStep === 'scope'
      ? true
      : currentStep === 'scopeConfig'
        ? scopeConfigReady &&
          !selection.isResolving &&
          !selection.isLoadingOptions &&
          !selection.isLoadingAssetOptions
        : currentStep === 'review'
          ? !isDataLoading && !isValidating && !selection.isResolving
          : !isDataLoading;

  const handleGenerate = async () => {
    setValidationErrors([]);

    try {
      const result = await validateReport({
        risk_keys: selection.resolvedRiskKeys,
        status_filter: [RiskStatus.Opened],
        executive_summary_path: current.executiveSummaryPath || undefined,
        narratives_path: current.narrativesPath || undefined,
        group_by: current.groupBy,
      });

      if (!result.valid) {
        setValidationErrors(
          result.errors.filter(
            e =>
              e.type === 'missing_definition' || e.type === 'missing_phase_tags'
          )
        );
        return;
      }

      // Validation passed — persist config, start export, and close.
      persistFormValues(false);
      onOpenChange(false);
      await exportReport({
        risk_keys: selection.resolvedRiskKeys,
        status_filter: [RiskStatus.Opened],
        config: {
          title: current.title,
          report_type: current.reportType,
          client_name: current.clientName,
          sow: current.sow,
          target: current.target,
          start_date: current.startDate,
          end_date: current.endDate,
          report_date: getTodayISO(),
          draft: current.draft,
          retest: current.retest,
          version: current.version,
          footer: current.footer,
          confidential_label: current.confidentialLabel,
          finding_sort: current.findingSort,
          // Only send section_order when the operator actually customized it. The
          // standard order is sent as undefined so the backend skips reorderSections
          // entirely (no-op) and the report renders as it did before this feature —
          // preserving today's default output.
          section_order: isStandardSectionOrder(
            cleanSectionOrder(current.sectionOrder)
          )
            ? undefined
            : cleanSectionOrder(current.sectionOrder),
        },
        executive_summary_path: current.executiveSummaryPath || undefined,
        narratives_path: current.narrativesPath || undefined,
        appendix_path: current.appendixPath || undefined,
        export_format: current.exportFormat,
        group_by: current.groupBy,
      });
    } catch {
      // Error toast is handled by useExportReport / useValidateReport.
    }
  };

  // Next/Back move by key: find the current key's position in the (mode-aware)
  // step list, step by one, clamp, and set the resulting key.
  const goToOffset = (offset: number) => {
    const next = Math.min(Math.max(currentIndex + offset, 0), steps.length - 1);
    setCurrentStep(steps[next]);
  };

  const footerConfig = useWizardFooterConfig({
    canGoNext,
    canGoBack: currentIndex > 0,
    isPending: isValidating,
    isLastStep: currentIndex === steps.length - 1,
    onNext: () => goToOffset(1),
    onBack: () => goToOffset(-1),
    onCancel: () => onOpenChange(false),
    onSubmit: handleGenerate,
    labels: { submit: 'Generate Report', submitting: 'Generating...' },
  });

  const hasValidationErrors = validationErrors.length > 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogDescription>
          {currentStep === 'scopeConfig'
            ? scopeConfigDescription(selection.mode)
            : STEP_DESCRIPTIONS[currentStep]}
        </DialogDescription>
      </DialogHeader>

      <WizardStepper
        steps={steps.map(s => ({
          id: s,
          title:
            s === 'scopeConfig'
              ? scopeConfigTitle(selection.mode)
              : STEP_TITLES[s],
        }))}
        currentStep={currentIndex + 1}
        variant="dots"
      />

      {currentStep === 'scope' && (
        <WizardStepBody align="center">
          <RiskModeStep selection={selection} />
        </WizardStepBody>
      )}

      {currentStep === 'scopeConfig' && (
        <WizardStepBody
          align={
            selection.mode === 'specific' || selection.mode === 'byAsset'
              ? 'top'
              : 'center'
          }
        >
          <RiskScopeConfigStep selection={selection} />
        </WizardStepBody>
      )}

      {currentStep === 'basics' && (
        <WizardStepBody align="top">
          {isDataLoading ? (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-[60px] w-full" />
              <Skeleton className="h-[60px] w-full" />
              <Skeleton className="h-[60px] w-full" />
            </div>
          ) : (
            <ReportBasicsStep
              current={current}
              handleFieldChange={handleFieldChange}
            />
          )}
        </WizardStepBody>
      )}

      {currentStep === 'engagement' && (
        <WizardStepBody align="center">
          <ReportEngagementStep
            current={current}
            handleFieldChange={handleFieldChange}
          />
        </WizardStepBody>
      )}

      {currentStep === 'files' && (
        <WizardStepBody align="center">
          <ReportFilesStep
            current={current}
            fileVariants={fileVariants}
            handleFieldChange={handleFieldChange}
          />
        </WizardStepBody>
      )}

      {currentStep === 'review' && (
        <WizardStepBody align="top">
          <ReviewStep
            current={current}
            selection={selection}
            validationErrors={validationErrors}
            hasValidationErrors={hasValidationErrors}
          />
        </WizardStepBody>
      )}

      <WizardModalFooter config={footerConfig} />
    </>
  );
}

type ReportConfigState = ReturnType<typeof useReportConfigForm>;

function ReportBasicsStep({
  current,
  handleFieldChange,
}: {
  current: ReportConfigState['current'];
  handleFieldChange: ReportConfigState['handleFieldChange'];
}) {
  // Filter out unknown/removed keys (e.g. legacy "taxonomies" from a persisted
  // pdf-export-options value) so they never render as a row or get reordered.
  const sections = cleanSectionOrder(current.sectionOrder);

  // "default" hides the reorder list and keeps the standard section order;
  // "custom" reveals it. Seed from whether the persisted order is non-standard.
  const [sectionMode, setSectionMode] = useState<'default' | 'custom'>(() =>
    isStandardSectionOrder(sections) ? 'default' : 'custom'
  );

  const onSectionModeChange = (mode: string) => {
    if (mode === 'default') {
      setSectionMode('default');
      handleFieldChange('sectionOrder', [...DEFAULT_SECTION_ORDER]);
    } else {
      setSectionMode('custom');
    }
  };

  // Pure splice reorder (mirrors useAttackStages.ts reorderStages). Returns a
  // new array and bails on out-of-range targets.
  const moveSection = (from: number, to: number) => {
    if (to < 0 || to >= sections.length) return;
    const updated = [...sections];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    handleFieldChange('sectionOrder', updated);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Report Title */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-title">Report Title</Label>
        <Input
          id="report-title"
          type="text"
          value={current.title}
          onChange={e => handleFieldChange('title', e.target.value)}
          placeholder="Client Penetration Test"
        />
      </div>

      {/* Report Type (optional free-text descriptor) */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="report-type">Report Type</Label>
          <Tooltip title="Optional report-template selector. Leave blank for the default template; a value with no matching template will fail report generation.">
            <InformationCircleIcon className="text-chariot-stroke size-4" />
          </Tooltip>
        </div>
        <Input
          id="report-type"
          type="text"
          value={current.reportType}
          onChange={e => handleFieldChange('reportType', e.target.value)}
          placeholder="nrf | nrf-prelim | iot-summary (leave blank for standard)"
        />
      </div>

      {/* Format toggle */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-format">Format</Label>
        <Select
          value={current.exportFormat}
          onValueChange={value =>
            handleFieldChange(
              'exportFormat',
              value as typeof current.exportFormat
            )
          }
        >
          <SelectTrigger id="report-format" aria-label="Format">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ExportFormat.PDF}>PDF</SelectItem>
            <SelectItem value={ExportFormat.ZIP}>ZIP</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Group Findings By */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-group-by">Group Findings By</Label>
        <Select
          value={current.groupBy}
          onValueChange={value => handleFieldChange('groupBy', value)}
        >
          <SelectTrigger id="report-group-by" aria-label="Group Findings By">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="attack_surface">Attack Surface</SelectItem>
            <SelectItem value="tag">Tags (Phase)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Finding order — sort criterion within each phase */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-finding-sort">Finding order</Label>
        <Select
          value={current.findingSort}
          onValueChange={value => handleFieldChange('findingSort', value)}
        >
          <SelectTrigger id="report-finding-sort" aria-label="Finding order">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FINDING_SORT_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Section order — Default vs Custom; Custom reveals the reorder list */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="report-section-order">Section order</Label>
        <Select value={sectionMode} onValueChange={onSectionModeChange}>
          <SelectTrigger id="report-section-order" aria-label="Section order">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="custom">Custom…</SelectItem>
          </SelectContent>
        </Select>

        {sectionMode === 'custom' && (
          <ul
            aria-label="Section order"
            className="border-chariot-stroke divide-chariot-stroke mt-1.5 flex flex-col divide-y rounded-md border"
          >
            {sections.map((key, index) => (
              <li
                key={key}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="text-chariot-text-primary text-sm">
                  {SECTION_LABELS[key] ?? key}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${SECTION_LABELS[key] ?? key} up`}
                    disabled={index === 0}
                    onClick={() => moveSection(index, index - 1)}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${SECTION_LABELS[key] ?? key} down`}
                    disabled={index === sections.length - 1}
                    onClick={() => moveSection(index, index + 1)}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReportEngagementStep({
  current,
  handleFieldChange,
}: {
  current: ReportConfigState['current'];
  handleFieldChange: ReportConfigState['handleFieldChange'];
}) {
  return (
    <div className="flex flex-col gap-5">
      {/* Client Name + Target */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-client-name">Client Name</Label>
          <Input
            id="report-client-name"
            type="text"
            value={current.clientName}
            onChange={e => handleFieldChange('clientName', e.target.value)}
            placeholder="Client Inc"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-target">Target</Label>
          <Input
            id="report-target"
            type="text"
            value={current.target}
            onChange={e => handleFieldChange('target', e.target.value)}
            placeholder="example.com"
          />
        </div>
      </div>

      {/* Engagement Start / End dates (display-only on the report) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-start-date">Start Date</Label>
          <StyledDatePicker
            name="report-start-date"
            value={current.startDate}
            onChange={value => handleFieldChange('startDate', value)}
            max={current.endDate || undefined}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-end-date">End Date</Label>
          <StyledDatePicker
            name="report-end-date"
            value={current.endDate}
            onChange={value => handleFieldChange('endDate', value)}
            min={current.startDate || undefined}
          />
        </div>
      </div>
      <p className="text-chariot-text-secondary -mt-3.5 text-xs">
        Engagement period shown on the report. Defaults to the customer&apos;s
        subscription term.
      </p>

      {/* Draft + Retest */}
      <div className="flex flex-col">
        <div className="flex items-center gap-3 py-2.5">
          <Checkbox
            name="draft"
            label="Draft"
            value={current.draft ? 'true' : 'false'}
            onChange={() => handleFieldChange('draft', !current.draft)}
            showTick
          />
          <span className="text-chariot-text-secondary text-xs">
            Adds a DRAFT watermark to the generated report
          </span>
        </div>
        <div className="flex items-center gap-3 py-2.5">
          <Checkbox
            name="retest"
            label="Retest"
            value={current.retest ? 'true' : 'false'}
            onChange={() => handleFieldChange('retest', !current.retest)}
            showTick
          />
          <span className="text-chariot-text-secondary text-xs">
            Include retest status badges and sections in the report
          </span>
        </div>
      </div>
    </div>
  );
}

function ReportFilesStep({
  current,
  fileVariants,
  handleFieldChange,
}: {
  current: ReportConfigState['current'];
  fileVariants: ReportConfigState['fileVariants'];
  handleFieldChange: ReportConfigState['handleFieldChange'];
}) {
  return (
    <div className="flex flex-col gap-5">
      {/* Executive Summary file select */}
      <ReportFileSelect
        label="Executive Summary"
        value={current.executiveSummaryPath}
        options={fileVariants.executiveSummary}
        extraOptions={[
          {
            value: AUTO_EXECUTIVE_SUMMARY,
            label: 'Auto-generate from findings (AI)',
          },
        ]}
        onChange={value => handleFieldChange('executiveSummaryPath', value)}
      />

      {/* Narratives file select */}
      <ReportFileSelect
        label="Narratives"
        value={current.narrativesPath}
        options={fileVariants.narratives}
        onChange={value => handleFieldChange('narrativesPath', value)}
      />

      {/* Appendix file select */}
      <ReportFileSelect
        label="Appendix"
        value={current.appendixPath}
        options={fileVariants.appendix}
        onChange={value => handleFieldChange('appendixPath', value)}
      />
    </div>
  );
}

function ReviewStep({
  current,
  selection,
  validationErrors,
  hasValidationErrors,
}: {
  current: ReportConfigState['current'];
  selection: ReturnType<typeof useRiskSelection>;
  validationErrors: ValidateReportError[];
  hasValidationErrors: boolean;
}) {
  const coverageLabel = (() => {
    if (selection.mode === 'all') return 'All open risks';
    const count = selection.resolvedRiskKeys?.length ?? 0;
    if (selection.isResolving) return 'Resolving matching risks…';
    if (count === 0) return 'All open risks';
    return `${count} risk${count === 1 ? '' : 's'}`;
  })();

  const formatLabel = current.exportFormat === ExportFormat.ZIP ? 'ZIP' : 'PDF';
  const groupByLabel =
    current.groupBy === 'tag' ? 'Tags (Phase)' : 'Attack Surface';
  const findingSortLabel =
    FINDING_SORT_OPTIONS.find(o => o.value === current.findingSort)?.label ??
    current.findingSort;
  const sectionOrderLabel = isStandardSectionOrder(
    cleanSectionOrder(current.sectionOrder)
  )
    ? 'Default'
    : 'Custom';

  return (
    <div className="flex flex-col gap-5">
      {hasValidationErrors && (
        <div className="border-chariot-status-critical bg-chariot-status-critical/5 rounded-md border px-4 py-3">
          <p className="text-chariot-status-critical mb-2 text-sm font-semibold">
            Validation errors — please resolve before generating:
          </p>
          {validationErrors.some(e => e.type === 'missing_definition') && (
            <div>
              <p className="text-chariot-text-primary mb-1 text-sm">
                Missing definitions for the following risks:
              </p>
              <ul className="list-disc pl-5 text-sm">
                {validationErrors
                  .filter(e => e.type === 'missing_definition')
                  .map(err => (
                    <li
                      key={err.risk_key}
                      className="text-chariot-text-secondary"
                    >
                      {err.risk_name}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {validationErrors.some(e => e.type === 'missing_phase_tags') && (
            <div>
              <p className="text-chariot-text-primary mb-1 text-sm">
                The following findings are missing phase tags. Add a{' '}
                <code className="text-xs">phase_*</code> tag to each before
                exporting with tag-based grouping:
              </p>
              <ul className="list-disc pl-5 text-sm">
                {validationErrors
                  .filter(e => e.type === 'missing_phase_tags')
                  .map(err => (
                    <li
                      key={err.risk_key}
                      className="text-chariot-text-secondary"
                    >
                      {err.risk_name}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <dl className="divide-chariot-stroke border-chariot-stroke flex flex-col divide-y rounded-lg border">
        <ReviewRow label="Risks covered" value={coverageLabel} />
        <ReviewRow label="Report title" value={current.title || '—'} />
        <ReviewRow label="Report type" value={current.reportType || '—'} />
        <ReviewRow label="Format" value={formatLabel} />
        <ReviewRow label="Group findings by" value={groupByLabel} />
        <ReviewRow label="Finding order" value={findingSortLabel} />
        <ReviewRow label="Section order" value={sectionOrderLabel} />
        <ReviewRow label="Client name" value={current.clientName || '—'} />
        <ReviewRow label="Target" value={current.target || '—'} />
        <ReviewRow
          label="Engagement period"
          value={
            current.startDate || current.endDate
              ? `${current.startDate || '—'} → ${current.endDate || '—'}`
              : '—'
          }
        />
        <ReviewRow
          label="Draft watermark"
          value={current.draft ? 'Yes' : 'No'}
        />
      </dl>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
      <dt className="text-chariot-text-secondary text-sm">{label}</dt>
      <dd className="text-chariot-text-primary truncate text-right text-sm font-medium">
        {value}
      </dd>
    </div>
  );
}

function ReportFileSelect({
  label,
  value,
  options,
  extraOptions = [],
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  /** Permanent non-file options rendered between (None) and the file list. */
  extraOptions?: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`report-file-${label}`}>{label}</Label>
      <Select
        value={value || NONE_VALUE}
        onValueChange={v => onChange(v === NONE_VALUE ? '' : v)}
      >
        <SelectTrigger id={`report-file-${label}`} aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>(None)</SelectItem>
          {extraOptions.map(o => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
          {options.map(f => (
            <SelectItem key={f} value={f}>
              {f}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
